import { useState } from 'react'
import type { Wine } from '../../types'
import type { WineResult } from '../../utils/scoring'

type WineResultCardProps = {
  result: WineResult
  wineCount: number
  sortOrder: 'wine' | 'rank'
  wines: Wine[]
  wineAssignments: (number | null)[]
  onAssign: (wineIndex: number | null) => void
  dimmed?: boolean
}

function formatOrdinal(n: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0])
}

export function WineResultCard({ result, wineCount, sortOrder, wines, wineAssignments, onAssign, dimmed }: WineResultCardProps) {
  const [activePopover, setActivePopover] = useState<number | null>(null)
  const [isSelectOpen, setIsSelectOpen] = useState(false)

  const handleChipClick = (score: number) => {
    setActivePopover(activePopover === score ? null : score)
  }

  const handleBackdropClick = () => {
    setActivePopover(null)
  }

  // Find which wine is assigned to this letter
  const assignedWineIndex = wineAssignments[result.index]
  const isAssigned = assignedWineIndex !== null

  // Get available wines (not assigned elsewhere, or currently assigned to this letter)
  const availableWines = wines
    .map((wine, idx) => ({ wine, idx }))
    .filter(({ idx }) => {
      const assignedToLetter = wineAssignments.indexOf(idx)
      return assignedToLetter === -1 || assignedToLetter === result.index
    })

  const circleContent = sortOrder === 'wine' ? result.letter : `#${result.rank}`
  const unassignedText = `Wine ${result.letter} came in ${formatOrdinal(result.rank)}`
  const nameContent = isAssigned
    ? sortOrder === 'wine'
      ? `#${result.rank} - ${result.name}`
      : `Wine ${result.letter} - ${result.name}`
    : unassignedText

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    if (value === '') {
      onAssign(null)
    } else {
      onAssign(parseInt(value, 10))
    }
    setIsSelectOpen(false)
  }

  return (
    <div
      className="p-4 border border-black/10 rounded-lg bg-white transition-opacity duration-300"
      style={{ opacity: dimmed ? 0.35 : 1 }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-black text-cream font-bold">
            {circleContent}
          </div>
          <div>
            <div className="font-medium">
              {nameContent}
            </div>
            <div className="text-sm text-black/60">{result.totalPoints} points</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAssigned && result.price !== undefined && (
            <div className="text-black/60">${result.price}</div>
          )}
          {isSelectOpen ? (
            <select
              autoFocus
              value={assignedWineIndex ?? ''}
              onChange={handleSelectChange}
              onBlur={() => setIsSelectOpen(false)}
              className="px-2 py-1 text-sm border border-black/20 rounded bg-white focus:outline-none focus:border-black"
            >
              <option value="">-- Select wine --</option>
              {availableWines.map(({ wine, idx }) => (
                <option key={idx} value={idx}>
                  {wine.name}
                </option>
              ))}
            </select>
          ) : (
            <button
              onClick={() => {
                if (!isAssigned && availableWines.length === 1) {
                  onAssign(availableWines[0].idx)
                } else {
                  setIsSelectOpen(true)
                }
              }}
              className="px-2 py-1 text-sm bg-black/5 rounded hover:bg-black/10 transition-colors"
            >
              {isAssigned ? 'Change' : 'Assign'}
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 relative">
        {([
          { score: 1, label: '1st', color: '#DAA520' },
          { score: 2, label: '2nd', color: '#A0A0A0' },
        ] as const).map(({ score, label, color }) => {
          const tasters = result.scoreDistribution.get(score) ?? []

          return (
            <div key={score} className="relative">
              <button
                onClick={() => handleChipClick(score)}
                className="relative z-20 px-2 py-1 text-sm bg-black/5 rounded hover:bg-black/10 transition-colors flex items-center gap-1.5"
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                {tasters.length}x {label}
              </button>
              {activePopover === score && tasters.length > 0 && (
                <>
                  <div className="fixed inset-0 z-10" onClick={handleBackdropClick} />
                  <div className="absolute left-0 top-full mt-1 z-20 px-3 py-2 bg-black text-cream text-sm rounded shadow-lg whitespace-nowrap">
                    {tasters.join(', ')}
                  </div>
                </>
              )}
            </div>
          )
        })}
        {wineCount > 3 && (
          <div className="flex items-center gap-1 px-1">
            {Array.from({ length: Math.min(wineCount - 3, 10) }, (_, i) => (
              <span key={i} className="inline-block w-1.5 h-1.5 rounded-full bg-black/10" />
            ))}
          </div>
        )}
        {(() => {
          const tasters = result.scoreDistribution.get(wineCount) ?? []
          return (
            <div className="relative">
              <button
                onClick={() => handleChipClick(wineCount)}
                className="relative z-20 px-2 py-1 text-sm bg-black/5 rounded hover:bg-black/10 transition-colors flex items-center gap-1.5"
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#DC2626' }} />
                {tasters.length}x last
              </button>
              {activePopover === wineCount && tasters.length > 0 && (
                <>
                  <div className="fixed inset-0 z-10" onClick={handleBackdropClick} />
                  <div className="absolute left-0 top-full mt-1 z-20 px-3 py-2 bg-black text-cream text-sm rounded shadow-lg whitespace-nowrap">
                    {tasters.join(', ')}
                  </div>
                </>
              )}
            </div>
          )
        })()}
      </div>
    </div>
  )
}
